import { ArrowLeft, Image, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EventType, ImportanceLevel, ITags } from "../../../interfaces/signal";
import {
    createAdminSignal,
    updateAdminSignal,
    getAdminSignal,
} from "../../../interfaces/admin/signals";
import { getCompanyFilterValues } from "../../../interfaces/companies";
import BONavigation from "../../../components/admin/BONavigation";

export default function AdminSignalForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        eventType: "seed" as EventType,
        title: "",
        description: "",
        company: "",
        companyWebsite: "",
        companyLogo: "",
        companyLocation: "",
        companyIndustry: "",
        contactFirstName: "",
        contactLastName: "",
        contactEmail: "",
        contactLinkedin: "",
        amount: "",
        currency: "USD",
        investors: [] as string[],
        valuation: "",
        source: "",
        sourceUrl: "",
        imageUrl: "",
        publishedAt: "",
        importance: "medium" as ImportanceLevel,
        tags: { franchise: "", categories: [] as string[], subCategories: [] as string[] } as ITags,
        isActive: true,
        expiresAt: "",
        alreadyContacted: false,
    });

    const [investorInput, setInvestorInput] = useState("");
    const [uploadingImage, setUploadingImage] = useState(false);
    const [showFinancialDetails, setShowFinancialDetails] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Tag inputs and suggestions (franchise / categories / subCategories)
    const [categoryInput, setCategoryInput] = useState("");
    const [subCategoryInput, setSubCategoryInput] = useState("");
    const [tagSuggestions, setTagSuggestions] = useState<{ franchises: string[]; categories: string[]; subCategories: string[] }>({ franchises: [], categories: [], subCategories: [] });

    useEffect(() => {
        if (isEdit && id) {
            fetchSignal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Load tag suggestions from database (franchise / categories / subCategories)
    useEffect(() => {
        const loadTagSuggestions = async () => {
            try {
                const tags = await getCompanyFilterValues('tags');
                setTagSuggestions(tags);
            } catch (error) {
                console.error('Error loading tag suggestions:', error);
            }
        };
        loadTagSuggestions();
    }, []);

    const fetchSignal = async () => {
        try {
            setLoading(true);
            const signal = await getAdminSignal(id!);
            setFormData({
                eventType: signal.eventType,
                title: signal.title,
                description: signal.description || "",
                company: signal.company,
                companyWebsite: signal.companyWebsite || "",
                companyLogo: signal.companyLogo || "",
                companyLocation: signal.companyLocation || "",
                companyIndustry: signal.companyIndustry || "",
                contactFirstName: (signal as any).contactFirstName || "",
                contactLastName: (signal as any).contactLastName || "",
                contactEmail: (signal as any).contactEmail || "",
                contactLinkedin: (signal as any).contactLinkedin || "",
                amount: signal.amount?.toString() || "",
                currency: signal.currency || "USD",
                investors: signal.investors || [],
                valuation: signal.valuation?.toString() || "",
                source: signal.source || "",
                sourceUrl: signal.sourceUrl || "",
                imageUrl: signal.imageUrl || "",
                publishedAt: signal.publishedAt ? new Date(signal.publishedAt).toISOString().split('T')[0] : "",
                importance: signal.importance,
                tags: signal.tags || { franchise: "", categories: [], subCategories: [] },
                isActive: signal.isActive !== false,
                expiresAt: signal.expiresAt ? new Date(signal.expiresAt).toISOString().split('T')[0] : "",
                alreadyContacted: signal.alreadyContacted || false,
            });
        } catch (err) {
            console.error("Failed to fetch signal:", err);
            setError("Failed to load signal");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        try {
            setLoading(true);

            const payload: any = {
                ...formData,
                amount: formData.amount ? parseFloat(formData.amount) : undefined,
                valuation: formData.valuation ? parseFloat(formData.valuation) : undefined,
                publishedAt: formData.publishedAt ? new Date(formData.publishedAt) : undefined,
                expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : undefined,
            };

            if (isEdit && id) {
                await updateAdminSignal(id, payload);
            } else {
                await createAdminSignal(payload);
            }

            navigate("/bo/signals");
        } catch (err: any) {
            console.error("Failed to save signal:", err);
            setError(err.response?.data?.error || "Failed to save signal");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value
        }));
    };

    const addInvestor = () => {
        if (investorInput.trim()) {
            setFormData(prev => ({
                ...prev,
                investors: [...prev.investors, investorInput.trim()]
            }));
            setInvestorInput("");
        }
    };

    const removeInvestor = (index: number) => {
        setFormData(prev => ({
            ...prev,
            investors: prev.investors.filter((_, i) => i !== index)
        }));
    };

    // Handlers for structured tags
    const handleAddCategory = () => {
        if (categoryInput.trim()) {
            setFormData(prev => ({
                ...prev,
                tags: {
                    ...(prev.tags as ITags),
                    categories: [...((prev.tags as ITags).categories || []), categoryInput.trim()]
                }
            }));
            setCategoryInput("");
        }
    };

    const handleRemoveCategory = (category: string) => {
        setFormData(prev => ({
            ...prev,
            tags: {
                ...(prev.tags as ITags),
                categories: ((prev.tags as ITags).categories || []).filter(c => c !== category)
            }
        }));
    };

    const handleAddSubCategory = () => {
        if (subCategoryInput.trim()) {
            setFormData(prev => ({
                ...prev,
                tags: {
                    ...(prev.tags as ITags),
                    subCategories: [...((prev.tags as ITags).subCategories || []), subCategoryInput.trim()]
                }
            }));
            setSubCategoryInput("");
        }
    };

    const handleRemoveSubCategory = (subCategory: string) => {
        setFormData(prev => ({
            ...prev,
            tags: {
                ...(prev.tags as ITags),
                subCategories: ((prev.tags as ITags).subCategories || []).filter(sc => sc !== subCategory)
            }
        }));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            return;
        }

        try {
            setUploadingImage(true);
            setError(null);

            // For now, convert to base64 for immediate preview
            // In production, you should upload to a proper image hosting service
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64 = reader.result as string;
                
                // Option 1: Use base64 directly (works but not recommended for large images in production)
                setFormData(prev => ({
                    ...prev,
                    imageUrl: base64
                }));
                
                setUploadingImage(false);
                
                // TODO: In production, upload to your image hosting service here
                // Example services: Cloudinary, imgbb, AWS S3, Cloudflare R2, etc.
                // Then set the returned URL instead of base64
            };
            
            reader.onerror = () => {
                setError('Failed to read image file');
                setUploadingImage(false);
            };
        } catch (err) {
            console.error('Failed to process image:', err);
            setError('Failed to process image');
            setUploadingImage(false);
        }
    };

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            imageUrl: ''
        }));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const eventTypes: { value: EventType; label: string }[] = [
        { value: "seed", label: "Seed Funding" },
        { value: "series_a", label: "Series A" },
        { value: "series_b", label: "Series B" },
        { value: "series_c", label: "Series C" },
        { value: "series_d", label: "Series D" },
        { value: "10-month-ecom", label: "10 Month E-commerce" },
        { value: "ipo", label: "IPO" },
        { value: "acquisition", label: "Acquisition" },
        { value: "merger", label: "Merger" },
        { value: "partnership", label: "Partnership" },
        { value: "expansion", label: "Expansion" },
        { value: "product_launch", label: "Product Launch" },
        { value: "hiring", label: "Hiring" },
        { value: "executive_change", label: "Executive Change" },
        { value: "other", label: "Other" },
    ];

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-4xl px-4 py-8">
                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate("/bo/signals")}
                        className="flex items-center gap-2 text-gray-600 hover:text-black mb-4"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Signals
                    </button>
                    <h1 className="text-2xl font-semibold text-black">
                        {isEdit ? "Edit Signal" : "Create New Signal"}
                    </h1>
                </div>

                {/* Tabs */}
                <BONavigation />

                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xs text-red-700 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Information */}
                    <div className="bg-white border rounded-xs p-6">
                        <h2 className="text-lg font-semibold text-black mb-4">Basic Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                    placeholder="e.g., Acme Corp raises $10M Series A"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                    placeholder="Additional context about the event..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Event Type *
                                </label>
                                <select
                                    name="eventType"
                                    value={formData.eventType}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                >
                                    {eventTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {/* <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Importance *
                                </label>
                                <select
                                    name="importance"
                                    value={formData.importance}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                >
                                    {importanceLevels.map(level => (
                                        <option key={level.value} value={level.value}>
                                            {level.label}
                                        </option>
                                    ))}
                                </select>
                            </div> */}
                        </div>
                    </div>

                    {/* Company Information */}
                    <div className="bg-white border rounded-xs p-6">
                        <h2 className="text-lg font-semibold text-black mb-4">Company Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Company Name *
                                </label>
                                <input
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Website
                                </label>
                                <input
                                    type="url"
                                    name="companyWebsite"
                                    value={formData.companyWebsite}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Location
                                </label>
                                <input
                                    type="text"
                                    name="companyLocation"
                                    value={formData.companyLocation}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Industry
                                </label>
                                <input
                                    type="text"
                                    name="companyIndustry"
                                    value={formData.companyIndustry}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white border rounded-xs p-6">
                        <h2 className="text-lg font-semibold text-black mb-4">Contact Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    name="contactFirstName"
                                    value={formData.contactFirstName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                    placeholder="John"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    name="contactLastName"
                                    value={formData.contactLastName}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                    placeholder="Doe"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    name="contactEmail"
                                    value={formData.contactEmail}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                    placeholder="john@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    LinkedIn URL
                                </label>
                                <input
                                    type="url"
                                    name="contactLinkedin"
                                    value={formData.contactLinkedin}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                    placeholder="https://linkedin.com/in/johndoe"
                                />
                            </div>
                        </div>
                    </div>
                

                    {/* Source & Metadata */}
                    <div className="bg-white border rounded-xs p-6">
                        <h2 className="text-lg font-semibold text-black mb-4">Source & Metadata</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Source
                                </label>
                                <input
                                    type="text"
                                    name="source"
                                    value={formData.source}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                    placeholder="e.g., TechCrunch, LinkedIn"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Source URL
                                </label>
                                <input
                                    type="url"
                                    name="sourceUrl"
                                    value={formData.sourceUrl}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Image URL (Featured Image)
                                </label>
                                
                                {/* URL Input */}
                                <input
                                    type="url"
                                    name="imageUrl"
                                    value={formData.imageUrl}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none mb-2"
                                    placeholder="https://example.com/image.jpg"
                                />
                                
                                {/* OR Divider */}
                                <div className="flex items-center gap-3 my-3">
                                    <div className="flex-1 border-t border-gray-300"></div>
                                    <span className="text-xs text-gray-500 font-medium">OR UPLOAD</span>
                                    <div className="flex-1 border-t border-gray-300"></div>
                                </div>
                                
                                {/* File Upload */}
                                <div className="flex gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        id="imageUpload"
                                    />
                                    <label
                                        htmlFor="imageUpload"
                                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xs text-sm cursor-pointer hover:border-gray-400 hover:bg-[#F9F6F3] transition-colors ${uploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <Image className="w-5 h-5 text-gray-400" />
                                        <span className="text-gray-600">
                                            {uploadingImage ? 'Uploading...' : 'Choose Image File'}
                                        </span>
                                    </label>
                                    {formData.imageUrl && (
                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            className="px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xs hover:bg-red-100 transition-colors"
                                            title="Remove image"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Max file size: 5MB. Supported: JPG, PNG, GIF, WebP
                                </p>
                                
                                {/* Preview */}
                                {formData.imageUrl && (
                                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-xs">
                                        <p className="text-xs font-medium text-gray-700 mb-2">Preview:</p>
                                        <img 
                                            src={formData.imageUrl} 
                                            alt="Preview" 
                                            className="h-40 w-full object-cover rounded border border-gray-300"
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                e.currentTarget.parentElement!.innerHTML += '<p class="text-xs text-red-600">Failed to load image</p>';
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Published Date
                                </label>
                                <input
                                    type="datetime-local"
                                    name="publishedAt"
                                    value={formData.publishedAt}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Expires At (Optional)
                                </label>
                                <input
                                    type="date"
                                    name="expiresAt"
                                    value={formData.expiresAt}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                />
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="mt-4">
                            <h3 className="text-md font-medium text-gray-900 mb-3">Tags</h3>

                            {/* Franchise */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Franchise</label>
                                <input
                                    type="text"
                                    list="franchise-suggestions"
                                    value={(formData.tags as ITags).franchise || ""}
                                    onChange={(e) => setFormData(prev => ({ ...prev, tags: { ...(prev.tags as ITags), franchise: e.target.value } }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                    placeholder="e.g., Subway, McDonald's..."
                                />
                                <datalist id="franchise-suggestions">
                                    {tagSuggestions.franchises.map((franchise, index) => (
                                        <option key={index} value={franchise} />
                                    ))}
                                </datalist>
                            </div>

                            {/* Categories */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Catégories</label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        list="category-suggestions"
                                        value={categoryInput}
                                        onChange={(e) => setCategoryInput(e.target.value)}
                                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                        placeholder="Ajouter une catégorie..."
                                    />
                                    <button type="button" onClick={handleAddCategory} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">Ajouter</button>
                                </div>
                                <datalist id="category-suggestions">
                                    {tagSuggestions.categories.map((category, index) => (
                                        <option key={index} value={category} />
                                    ))}
                                </datalist>
                                {((formData.tags as ITags).categories || []).length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {(formData.tags as ITags).categories!.map((category, index) => (
                                            <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-800 rounded-full border border-green-200">
                                                {category}
                                                <button type="button" onClick={() => handleRemoveCategory(category)} className="hover:text-green-900">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* SubCategories */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sous-catégories</label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        list="subcategory-suggestions"
                                        value={subCategoryInput}
                                        onChange={(e) => setSubCategoryInput(e.target.value)}
                                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubCategory(); } }}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                                        placeholder="Ajouter une sous-catégorie..."
                                    />
                                    <button type="button" onClick={handleAddSubCategory} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">Ajouter</button>
                                </div>
                                <datalist id="subcategory-suggestions">
                                    {tagSuggestions.subCategories.map((subCategory, index) => (
                                        <option key={index} value={subCategory} />
                                    ))}
                                </datalist>
                                {((formData.tags as ITags).subCategories || []).length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {(formData.tags as ITags).subCategories!.map((subCategory, index) => (
                                            <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-800 rounded-full border border-purple-200">
                                                {subCategory}
                                                <button type="button" onClick={() => handleRemoveSubCategory(subCategory)} className="hover:text-purple-900">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Active Status */}
                        <div className="mt-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleChange}
                                    className="rounded border-gray-300"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Active (visible to users)
                                </span>
                            </label>
                        </div>

                        {/* Already Contacted */}
                        <div className="mt-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="alreadyContacted"
                                    checked={formData.alreadyContacted}
                                    onChange={handleChange}
                                    className="rounded border-gray-300"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Already Contacted (mark as unavailable in marketplace)
                                </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-6">
                                When checked, this signal will appear blurred and non-clickable to users, indicating someone has already contacted this prospect.
                            </p>
                        </div>
                    </div>

                    {/* Financial Details */}
                    <div className="bg-white border rounded-xs p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-black">Financial Details</h2>
                            <button
                                type="button"
                                onClick={() => setShowFinancialDetails(!showFinancialDetails)}
                                className="text-sm text-gray-900 hover:underline font-medium"
                            >
                                {showFinancialDetails ? 'Hide Details' : 'Show Details'}
                            </button>
                        </div>
                        
                        {showFinancialDetails && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Amount
                                        </label>
                                        <input
                                            type="number"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                            placeholder="10000000"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Currency
                                        </label>
                                        <input
                                            type="text"
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Valuation
                                        </label>
                                        <input
                                            type="number"
                                            name="valuation"
                                            value={formData.valuation}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                        />
                                    </div>
                                </div>
                                
                                {/* Investors */}
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Investors
                                    </label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={investorInput}
                                            onChange={(e) => setInvestorInput(e.target.value)}
                                            onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addInvestor())}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xs text-sm focus:ring-0 focus:outline-none"
                                            placeholder="Add investor name..."
                                        />
                                        <button
                                            type="button"
                                            onClick={addInvestor}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-xs hover:bg-gray-200"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {formData.investors.map((investor, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                                            >
                                                {investor}
                                                <button
                                                    type="button"
                                                    onClick={() => removeInvestor(index)}
                                                    className="text-gray-900 hover:underline"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-4 justify-end">
                        <button
                            type="button"
                            onClick={() => navigate("/bo/signals")}
                            className="px-6 py-2 border border-gray-300 text-gray-700 text-sm rounded-xs hover:bg-[#F9F6F3]"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-black text-white text-sm rounded-xs hover:bg-gray-800 disabled:bg-gray-400"
                        >
                            {loading ? "Saving..." : isEdit ? "Update Signal" : "Create Signal"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
